import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Send, MessageSquarePlus, ChevronDown, ChevronUp } from 'lucide-react';
import * as pedidosApi from '@/api/pedidos.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { PageHeader } from '@/components/common/PageHeader';
import { useAuth } from '@/hooks/useAuth';
import { hasRole } from '@conversia/shared-types';
import { cn } from '@/lib/cn';
import { filasDeLaFicha, siguientesEstados } from '@/lib/pedido-ficha';
import { useGuia } from '@/components/guia/guia-contexto';
import type { BotonCopiloto } from '@/api/copiloto.api';

/**
 * "QUÉ QUIERES QUE CAMBIEMOS".
 *
 * A la izquierda se habla; a la derecha se ve lo que ya se pidió y cómo va.
 *
 * La conversación es lo primero a propósito. Un formulario de ocho campos hace
 * que la gente escriba "el botón no sirve" en el primero y deje los otros
 * siete vacíos. Hablando, el asistente puede ir preguntando lo que falta, y lo
 * que falta es justo lo que hace constructible un pedido.
 *
 * Cada pedido se abre y muestra la ficha ENTERA: quien lo pidió confirma que se
 * entendió, y quien lo trabaja no tiene que volver a preguntar.
 */

const ESTADO_LABEL: Record<pedidosApi.EstadoPedido, string> = {
  NUEVO: 'Recibido',
  EN_ANALISIS: 'Lo estamos viendo',
  ACEPTADO: 'Lo vamos a hacer',
  RECHAZADO: 'No lo vamos a hacer',
  HECHO: 'Listo',
};

const ESTADO_VARIANTE: Record<
  pedidosApi.EstadoPedido,
  'default' | 'success' | 'danger' | 'warning'
> = {
  NUEVO: 'default',
  EN_ANALISIS: 'warning',
  ACEPTADO: 'warning',
  RECHAZADO: 'danger',
  HECHO: 'success',
};

/** En el idioma del dueño, no P1/P2/P3. */
const URGENCIA_LABEL: Record<string, string> = {
  frena: 'No puede trabajar',
  molesta: 'Pierde tiempo',
  mejora: 'Sería mejor así',
};

interface Burbuja {
  quien: 'yo' | 'asistente';
  texto: string;
  /** "Muéstrame": cuando era una duda, la entrevista ofrece la guía. */
  botones?: BotonCopiloto[];
}

export function Pedidos() {
  const { user } = useAuth();
  // Sin sesion resuelta todavia, NO es admin. Asumir que si daria un instante
  // con los botones de decision visibles para cualquiera.
  const esAdmin = user ? hasRole(user.role, 'ADMIN') : false;
  const [borrador, setBorrador] = useState('');
  const [burbujas, setBurbujas] = useState<Burbuja[]>([]);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [respuestas, setRespuestas] = useState<Record<string, string>>({});
  const qc = useQueryClient();
  const guia = useGuia();
  const fin = useRef<HTMLDivElement>(null);

  const lista = useQuery({ queryKey: ['pedidos'], queryFn: () => pedidosApi.listar() });

  useEffect(() => {
    fin.current?.scrollIntoView({ behavior: 'smooth' });
  }, [burbujas]);

  const hablar = useMutation({
    mutationFn: pedidosApi.conversar,
    onSuccess: (r) => {
      setBurbujas((prev) => [...prev, { quien: 'asistente', texto: r.texto, botones: r.botones }]);
      // Puede haber guardado el pedido en este turno: se refresca la lista.
      void qc.invalidateQueries({ queryKey: ['pedidos'] });
    },
    onError: () =>
      setBurbujas((prev) => [
        ...prev,
        { quien: 'asistente', texto: 'No pude contestarte ahora. Prueba de nuevo.' },
      ]),
  });

  const responder = useMutation({
    mutationFn: ({
      id,
      estado,
      respuesta,
    }: {
      id: string;
      estado: pedidosApi.EstadoPedido;
      respuesta?: string;
    }) => pedidosApi.responder(id, estado, respuesta),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pedidos'] }),
  });

  function enviar(texto: string) {
    const t = texto.trim();
    if (!t || hablar.isPending) return;
    setBurbujas((prev) => [...prev, { quien: 'yo', texto: t }]);
    setBorrador('');
    hablar.mutate(t);
  }

  return (
    <div className="p-4 md:p-6">
      <PageHeader
        title="Qué quieres que cambiemos"
        description="Cuéntale al asistente qué no funciona como esperabas o qué te gustaría que hiciera. Él te va preguntando lo que hace falta."
      />

      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_26rem]">
        {/* ─────────────────── la conversación */}
        <Card className="flex h-[32rem] flex-col overflow-hidden">
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
            {burbujas.length === 0 && (
              <div className="mx-auto max-w-md py-10 text-center">
                <MessageSquarePlus className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                <p className="text-sm text-slate-500">
                  Cuéntame qué pasó, con tus palabras. No hace falta que sepas explicarlo bien: yo
                  te voy preguntando.
                </p>
              </div>
            )}

            {burbujas.map((b, i) => (
              <div
                key={i}
                className={cn('flex', b.quien === 'yo' ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[85%] rounded-lg px-3.5 py-2.5 text-sm whitespace-pre-wrap',
                    b.quien === 'yo'
                      ? 'bg-brand-600 text-white'
                      : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-100',
                  )}
                >
                  {b.texto}
                  {(b.botones ?? [])
                    .filter((boton) => boton.receta && guia.puedeUsar(boton.receta))
                    .map((boton) => (
                      <button
                        key={boton.receta}
                        type="button"
                        onClick={() => guia.iniciar(boton.receta!)}
                        className="mt-2 block rounded-full border border-brand-500 px-2.5 py-1 text-xs font-medium text-brand-700 transition hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-slate-700"
                      >
                        Muéstrame: {boton.etiqueta}
                      </button>
                    ))}
                </div>
              </div>
            ))}

            {hablar.isPending && (
              <div className="flex justify-start">
                <div className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-500 dark:bg-slate-800">
                  Pensando…
                </div>
              </div>
            )}
            <div ref={fin} />
          </div>

          <div className="flex items-end gap-2 border-t border-surface-border p-3 dark:border-slate-800">
            <textarea
              value={borrador}
              rows={1}
              data-guia="pedidos.escribir"
              onChange={(e) => setBorrador(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  enviar(borrador);
                }
              }}
              placeholder="Cuéntame qué quieres que cambiemos…"
              aria-label="Cuéntale al asistente"
              className="max-h-32 min-h-[2.5rem] min-w-0 flex-1 resize-y rounded-md border border-surface-border bg-transparent px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-700"
            />
            <Button aria-label="Enviar" onClick={() => enviar(borrador)} loading={hablar.isPending}>
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        {/* ─────────────────── lo que ya se pidió */}
        <Card className="flex h-[32rem] flex-col overflow-hidden">
          <div className="border-b border-surface-border px-4 py-3 dark:border-slate-800">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
              Lo que ya se pidió
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {lista.isLoading && <Spinner />}

            {!lista.isLoading && (lista.data?.pedidos.length ?? 0) === 0 && (
              <p className="px-1 py-4 text-sm text-slate-500">
                Todavía no hay pedidos. El primero que hagas aparece acá con su código.
              </p>
            )}

            {lista.data?.pedidos.map((p) => {
              const estaAbierto = abierto === p.id;
              const proximos = siguientesEstados(p.estado);
              return (
                <div
                  key={p.id}
                  className="mb-2 rounded-md border border-surface-border p-3 dark:border-slate-800"
                >
                  <button
                    type="button"
                    className="flex w-full items-start justify-between gap-2 text-left"
                    aria-expanded={estaAbierto}
                    onClick={() => setAbierto(estaAbierto ? null : p.id)}
                  >
                    <span className="min-w-0 flex-1 text-sm font-medium text-slate-700 dark:text-slate-200">
                      {p.titulo}
                    </span>
                    <Badge variant={ESTADO_VARIANTE[p.estado]}>{ESTADO_LABEL[p.estado]}</Badge>
                    {estaAbierto ? (
                      <ChevronUp className="h-4 w-4 shrink-0 text-slate-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
                    )}
                  </button>
                  <p className="mt-1 text-xs text-slate-400">
                    {p.codigo} · {p.nombre}
                    {p.pantalla ? ` · ${p.pantalla}` : ''}
                    {URGENCIA_LABEL[p.urgencia] ? ` · ${URGENCIA_LABEL[p.urgencia]}` : ''}
                  </p>

                  {estaAbierto && (
                    <dl className="mt-3 space-y-2 border-t border-surface-border pt-3 text-xs dark:border-slate-800">
                      {filasDeLaFicha(p).map((f) => (
                        <div key={f.etiqueta}>
                          <dt className="font-medium text-slate-500 dark:text-slate-400">
                            {f.etiqueta}
                          </dt>
                          <dd className="whitespace-pre-wrap text-slate-700 dark:text-slate-200">
                            {f.valor}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  )}

                  {p.respuesta && (
                    <p className="mt-2 rounded bg-slate-50 p-2 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      {p.respuesta}
                    </p>
                  )}

                  {/* Cambiar el estado es una decisión: solo admin. */}
                  {esAdmin && estaAbierto && proximos.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <textarea
                        value={respuestas[p.id] ?? ''}
                        onChange={(e) => setRespuestas((r) => ({ ...r, [p.id]: e.target.value }))}
                        rows={2}
                        placeholder="Respuesta para quien lo pidió (opcional)"
                        aria-label={`Respuesta al pedido ${p.codigo}`}
                        className="w-full resize-y rounded-md border border-surface-border bg-transparent px-2.5 py-1.5 text-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:border-slate-700"
                      />
                      <div className="flex flex-wrap gap-1.5">
                        {proximos.map((estado) => (
                          <Button
                            key={estado}
                            variant={estado === 'RECHAZADO' ? 'ghost' : 'outline'}
                            size="sm"
                            loading={responder.isPending && responder.variables?.id === p.id}
                            onClick={() =>
                              responder.mutate({
                                id: p.id,
                                estado,
                                respuesta: respuestas[p.id]?.trim() || undefined,
                              })
                            }
                          >
                            {ESTADO_LABEL[estado]}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
